import React, { useContext } from 'react';
import { Text, Box } from '@mantine/core';
import { useTheme } from '@emotion/react';
import { useMediaQuery } from 'react-responsive';
import useTranslation from 'next-translate/useTranslation';
import Trans from 'next-translate/Trans';
import { ContestStatus } from '@betterpool/api-types/contests-service';

import { Link } from '~/domains/common/components/Link/Link';
import { UserContext } from '~/components/providers/UserProvider';
import { PageHeaderBanner } from '~/domains/common/components/PageHeader/PageHeaderBanner';
import {
  PageHeader,
  PageHeaderContainer,
  PageHeaderTitle,
} from '~/domains/common/components/PageHeader/PageHeader';
import { PageHeaderAvatar } from '~/domains/common/components/PageHeader/PageHeaderAvatar';
import { getContestThumbnail } from '~/domains/contest/domains/common/utils/getContestThumbnail';
import { Routes } from '~/domains/common/constants/routes';
import { GroupToStack } from '~/domains/common/components/GroupToStack/GroupToStack';
import dt from '~/testing';
import { useContest } from '~/domains/contest/hooks/useContest';
import { getHandle } from '~/domains/user/utils/getHandle';
import { FEATURE } from '~/common/enums/feature.enum';
import { useIsFeatureEnabled } from '~/hooks/useIsFeatureEnabled';

import { getDeadlineFormat } from '../../utils/deadlineFormat';
import EditContestButton from '../EntryButton/EditContestButton';
import EditEntrantsButton from '../EntryButton/EditEntrantsButton';

import useStyles from './ContestHeader.styles';
import { ContestHeaderActions } from './ContestHeaderActions';
import { ContestHeaderContent } from './ContestHeaderContent';
import { useEnableBannerAndAvatar } from './hooks/useEnableBannerAndAvatar';
import { getContestBanner } from './utils/getContestBanner';

export type ContestHeaderProps = {
  contestId: string;
  isAllowedInLocation?: boolean;
  isSidePanel?: boolean;
  hideCTA?: boolean;
};

export function ContestHeader({
  contestId,
  isAllowedInLocation,
  isSidePanel,
  hideCTA,
}: ContestHeaderProps) {
  const { user } = useContext(UserContext);
  const { data: fullContest } = useContest();

  const commissioner = fullContest?.commissioner;

  const contestThumbnail = getContestThumbnail({
    contestThumbnailUrl: fullContest?.thumbnail_url,
    createdByAdmin: fullContest?.created_by_admin,
    avatarUrl: fullContest?.commissioner.avatarUrl,
  });
  const contestBanner = getContestBanner({
    contestBannerlUrl: fullContest?.banner_url,
    sport: fullContest.league?.sport,
  });

  const theme = useTheme();
  const isMobile = useMediaQuery({ maxWidth: theme.breakpoints.xs });
  const isTablet = useMediaQuery({ maxWidth: theme.breakpoints.sm });

  const { t } = useTranslation('contest');

  const isBannerAndAvatarDisplayed = useEnableBannerAndAvatar();
  const isContestCommissioner = commissioner?.id === user?.id;
  const { classes } = useStyles({ isSidePanel });
  const hasEditPermission = isContestCommissioner && fullContest.status === ContestStatus.SCHEDULED;
  const isEntertainmentOnly = fullContest.settings?.entertainmentOnlySettings?.enabled;
  const isEntrantManagerEnabled =
    useIsFeatureEnabled(FEATURE.ENABLE_ENTRANT_MANAGER) && fullContest?.manage_entrants;
  const isEditButtonVisible = fullContest.status === ContestStatus.SCHEDULED && hasEditPermission;

  return (
    <>
      {isBannerAndAvatarDisplayed && (
        <PageHeaderBanner src={contestBanner} isSidePanel={isSidePanel} />
      )}
      <PageHeader data-test-id={dt.contestDetail.page.header.container} isSidePanel={isSidePanel}>
        {isBannerAndAvatarDisplayed && (
          <PageHeaderAvatar src={contestThumbnail} isSidePanel={isSidePanel} />
        )}
        {isEditButtonVisible ? (
          <EditContestButton
            isHidden={!isTablet}
            className={classes.action}
            contest={fullContest}
          />
        ) : null}
        {isContestCommissioner && isEntrantManagerEnabled ? (
          <EditEntrantsButton
            isHidden={!isTablet}
            className={classes.action}
            contest={fullContest}
            isEditButtonHidden={!isEditButtonVisible}
          />
        ) : null}
        <PageHeaderTitle
          isSidePanel={isSidePanel}
          data-test-id={dt.contestDetail.page.header.title}
        >
          <GroupToStack stackProps={{ spacing: 0 }} forceMobile={isSidePanel}>
            <Text>{fullContest.name}</Text>
          </GroupToStack>
          {isMobile && (
            <Box className={classes.subHeader} data-test-id={dt.contestDetail.page.header.subtitle}>
              <Text component="span" display="block" className={classes.dimmedText}>
                {fullContest.created_by_admin ? (
                  <Link href={Routes.commissionerChannel(commissioner?.id)}>
                    {t('detail.about.info.adminCreator')}
                  </Link>
                ) : (
                  <Trans
                    i18nKey="contest:detail.header.title.owner"
                    values={{ owner: getHandle(commissioner) }}
                    components={{
                      link: <Link href={Routes.commissionerChannel(commissioner?.id)} />,
                    }}
                  />
                )}
              </Text>
              &nbsp;
              <Text color={theme.colors.gray[5]} fz={theme.fontSizes.xs} data-chromatic="ignore">
                {getDeadlineFormat(fullContest.start_date)}
              </Text>
            </Box>
          )}
        </PageHeaderTitle>
        <PageHeaderContainer isSidePanel={isSidePanel}>
          <ContestHeaderContent
            isEntertainmentOnly={isEntertainmentOnly}
            entries={fullContest.entries}
            fee={fullContest.entry_fee}
            username={getHandle(commissioner)}
            createdByAdmin={fullContest.created_by_admin}
            prize={fullContest.prize_pool}
            deadline={fullContest.start_date}
            ownerId={commissioner?.id}
            isSidePanel={isSidePanel}
            resizingMode={fullContest.resizing_mode}
          />
          <ContestHeaderActions
            isAllowedInLocation={isAllowedInLocation}
            isEntertainmentOnly={isEntertainmentOnly}
            isSidePanel={isSidePanel}
            status={fullContest.status}
            contestId={contestId}
            contestName={fullContest.name}
            entries={fullContest.entries}
            isAfterDeadline={fullContest.is_after_first_late_swap_deadline}
            isContestCommissioner={isContestCommissioner}
            hideCTA={hideCTA}
          />
        </PageHeaderContainer>
      </PageHeader>
    </>
  );
}
